import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'

import { BaseModalType } from '@context/ModalsContext'

import { Input } from '@ui/components/Input/Input'
import { Modal } from '@ui/components/Modal'

import { useCreateWatchlist } from '../../hooks/useCreateWatchlist'
import { useWatchlistNameValidation } from './hooks/useWatchlistNameValidation'

interface CreateWatchlistModalProps {
  ownerExternalId?: string
  existingNames?: string[]
  onCreated?: (name: string) => void
}

interface FormValues {
  name: string
}

export const CreateWatchlistModal: BaseModalType<CreateWatchlistModalProps> = ({
  close,
  ownerExternalId,
  existingNames = [],
  onCreated,
}) => {
  const { t } = useTranslation()
  const { mutateAsync: createWatchlist } = useCreateWatchlist()
  const validateName = useWatchlistNameValidation(existingNames)

  const { values, errors, setFieldValue, setErrors, handleSubmit, isSubmitting } =
    useFormik<FormValues>({
      initialValues: { name: '' },
      validate: validateName,
      onSubmit: async (values, { setSubmitting }) => {
        if (!ownerExternalId) return
        try {
          const trimmed = values.name.trim()
          await createWatchlist({
            name: trimmed,
            description: '',
            type: 'PRIVATE',
            kind: 'REGULAR',
            symbols: [],
            owner: { login: ownerExternalId, domain: 'default' },
          })
          onCreated?.(trimmed)
          close()
        } finally {
          setSubmitting(false)
        }
      },
    })

  const isSubmitDisabled = !ownerExternalId || isSubmitting || !values.name.trim() || !!errors.name

  return (
    <Modal>
      <Modal.Header>{t('Instruments.Create new watchlist')}</Modal.Header>
      <Modal.Body>
        <Input
          label=''
          fullWidth
          placeholder={t('Instruments.Watchlist name')}
          value={values.name}
          error={!!errors.name}
          hint={errors.name}
          onChange={(e) => {
            setFieldValue('name', e.target.value)
            if (errors.name) setErrors({ name: undefined })
          }}
        />
      </Modal.Body>
      <Modal.Footer>
        <Modal.Footer.Secondary onClick={close} disabled={isSubmitting}>
          {t('Cancel')}
        </Modal.Footer.Secondary>
        <Modal.Footer.Primary
          onClick={() => handleSubmit()}
          disabled={isSubmitDisabled}
          loading={isSubmitting}
        >
          {t('Instruments.Create')}
        </Modal.Footer.Primary>
      </Modal.Footer>
    </Modal>
  )
}
